import { COMMON_GENERAL_CONFIG } from "@common-shared/config/general.config"
import {
    type AvailableLookupConsumer,
    AvailableLookupType,
} from "@common-shared/config/lookup.config"
import { PolicyModality } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import type { IIndexable } from "@common-shared/types/general.types"
import { SortDirection } from "@common-shared/types/requestData/commonRequestData.types"
import type {
    IHealthProvider,
    IPayer,
    ISimplifiedPractice,
} from "@common-shared/types/responseData/commonResponseData.types"

import { FRONT_LOOKUP_CONFIG } from "@front-shared/config/lookup.config"
import type {
    ILookupRequestData,
    IPayersSupportedFeatures,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types"

import { HttpHelper } from "../helpers/http.helper"

export class LookupService {
    /**
     * Search for available cpt codes based on consumer
     */
    static async lookupCptCodes(
        consumer?: AvailableLookupConsumer,
        neededModalities = [PolicyModality.MENTAL_HEALTH],
    ): Promise<Partial<Record<PolicyModality, ISelectRenderedOption[]>>> {
        return LookupService.sendLookup(
            AvailableLookupType.cptCodes,
            undefined,
            consumer
                ? {
                      consumer,
                      neededModalities,
                  }
                : undefined,
        )
    }

    /**
     * Search for health providers
     */
    static async lookupHealthProviders(
        search = "",
    ): Promise<IHealthProvider[]> {
        return LookupService.sendLookup(
            AvailableLookupType.healthProviders,
            search,
        )
    }

    /**
     * Search for eligible payers (insurance names)
     */
    static async lookupPayers(
        search = "",
        fullList = false,
        allPayers = false,
        supportedFeatures?: IPayersSupportedFeatures,
        sort?: Record<string, SortDirection>,
    ): Promise<IPayer[]> {
        const jsonParams = {
            fullList,
            allPayers,
            sort,
            ...(supportedFeatures || {}),
        }

        return LookupService.sendLookup(
            AvailableLookupType.payers,
            search,
            jsonParams,
        )
    }

    /**
     * Predict a payer from a member ID prefix (first 3 characters).
     * Returns the resolved payer when a confident match exists, otherwise null.
     */
    static async predictPayerByMemberId(
        memberId: string,
    ): Promise<IPayer | null> {
        const memberIdPrefix = memberId.slice(
            0,
            FRONT_LOOKUP_CONFIG.memberIdPayerPredictionMinLength,
        )

        // Don't attempt lookup if member ID prefix is too short for any prediction rule to match
        if (
            memberIdPrefix.length <
            FRONT_LOOKUP_CONFIG.memberIdPayerPredictionMinLength
        ) {
            return null
        }

        const result = await LookupService.sendLookup(
            AvailableLookupType.payerPrediction,
            memberIdPrefix,
        )

        // sendLookup falls back to [] when the lookup payload is empty/null
        return result && !Array.isArray(result) ? (result as IPayer) : null
    }

    /**
     * Search for health providers with their practices
     */
    static async lookupHealthProvidersWithPractices(
        search = "",
    ): Promise<IHealthProvider[]> {
        return LookupService.sendLookup(
            AvailableLookupType.healthProviders,
            search,
            {
                withPractices: true,
            },
        )
    }

    /**
     * Search for simplified practices
     */
    static async lookupSimplifiedPractices(
        search = "",
    ): Promise<ISimplifiedPractice[]> {
        return LookupService.sendLookup(
            AvailableLookupType.simplifiedPractices,
            search,
        )
    }

    /**
     * Search average session rate by ZIP
     */
    static async lookupSessionRateByZip(zip?: string): Promise<{
        zip: string
        averageSessionRate: number
    } | null> {
        return LookupService.sendLookup(
            AvailableLookupType.zipSessionRate,
            zip || undefined,
        )
    }

    /**
     * Search for support query categories
     */
    static async lookupSupportTicketCategories(): Promise<
        ISelectRenderedOption[]
    > {
        return LookupService.sendLookup(
            AvailableLookupType.zendeskSupportCategories,
        )
    }

    /**
     * Search for labeling portal tags without values
     */
    static async lookupLabelingPortalTagsWithoutValues(
        search?: string,
    ): Promise<ISelectRenderedOption[]> {
        return LookupService.sendLookup(
            AvailableLookupType.labelingPortalTagsWithoutValues,
            search,
        )
    }

    /**
     * Search for labeling portal tags with values
     */
    static async lookupLabelingPortalTagsWithValues(
        search?: string,
    ): Promise<ISelectRenderedOption[]> {
        return LookupService.sendLookup(
            AvailableLookupType.labelingPortalTagsWithValues,
            search,
        )
    }

    /**
     * General method for sending lookup request
     */
    protected static async sendLookup(
        type: string,
        search = "",
        jsonParams?: IIndexable,
        apiBaseUrl = import.meta.env.VITE_API_URL,
        onStartLoading?: () => void,
        onStopLoading?: () => void,
    ): Promise<any> {
        onStartLoading?.()

        const data: ILookupRequestData = {
            type: type,
        }

        if (jsonParams) {
            data.jsonParams = JSON.stringify(jsonParams)
        }

        if (search) {
            data.search = search
        }

        // Check if apiBaseUrl has version set, if not add version to it
        if (!apiBaseUrl.match(/\/v\d+$/)) {
            apiBaseUrl += apiBaseUrl.endsWith("/")
                ? COMMON_GENERAL_CONFIG.defaultApiVersion
                : `/${COMMON_GENERAL_CONFIG.defaultApiVersion}`
        }

        const result = await HttpHelper.sendRequest(
            `${apiBaseUrl}/lookup`,
            data,
        )

        onStopLoading?.()

        return result.lookup || []
    }
}
